import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const hardwareServices = [
  {
    id: 1,
    title: "PCB Design",
    description: "Multi-layer, high-speed, and custom footprint printed circuit board designs tailored for extreme industrial and defense applications. From schematics to fabrication-ready gerbers.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 2,
    title: "Embedded System Design",
    description: "End-to-end firmware development and hardware-software integration for specialized microcontrollers, DSPs, and real-time operating systems (RTOS).",
    image: "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 3,
    title: "IoT Based Product Development",
    description: "Scalable Internet of Things architectures featuring low-latency telemetry, sensor fusion, and secure wireless communication protocols (LoRa, BLE, 5G).",
    image: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 4,
    title: "UGV & Drones Based System",
    description: "Custom mechanical, electrical, and control systems for Unmanned Ground Vehicles (UGV) and autonomous multi-rotor aerial drones.",
    image: "https://images.unsplash.com/photo-1473968512647-3e447244af8f?q=80&w=800&auto=format&fit=crop"
  }
];

const softwareServices = [
  {
    id: 5,
    title: "ROS 2 Based Robotics Apps",
    description: "Advanced kinematic modeling, SLAM navigation algorithms, and multi-sensor payload integration utilizing the industry-standard Robot Operating System 2.",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 6,
    title: "Edge AI System Design",
    description: "Deploying high-performance neural networks directly on edge computing devices (like NVIDIA Jetson) for ultra-fast, offline inference.",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 7,
    title: "AI/ML & Computer Vision",
    description: "Custom deep learning pipelines for object detection, anomaly recognition, and real-time visual tracking in complex industrial environments.",
    image: "https://images.unsplash.com/photo-1563207153-f403bf289096?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: 8,
    title: "Web & APP Based Solutions",
    description: "Enterprise-grade command dashboards and secure mobile companions built on modern tech stacks (React, Flutter) to command field hardware remotely.",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=800&auto=format&fit=crop"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const ServiceCard = ({ service }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ scale: 1.02, translateY: -5 }}
    className="relative group bg-white border border-gray-200/80 p-5 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_45px_rgba(0,0,0,0.08)] hover:border-blue-500/40 transition-all duration-300 flex flex-col h-full min-h-[360px] overflow-hidden"
  >
    {/* Real Image Container */}
    <div className="w-full h-40 mb-5 rounded-xl overflow-hidden bg-gray-100 border border-gray-100 shadow-sm relative shrink-0">
      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
    </div>
    
    <div className="relative z-10 flex flex-col h-full px-1">
      <h3 className="text-lg font-extrabold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300 tracking-tight">{service.title}</h3>
      
      <p className="text-xs text-gray-600 mb-6 flex-grow leading-relaxed font-normal">
        {service.description}
      </p>
      
      <div className="mt-auto border-t border-gray-100 pt-3 flex items-center justify-between group-hover:border-blue-100 transition-colors">
        <Link 
          to="/contact" 
          className="text-[10px] font-mono font-bold text-gray-500 hover:text-blue-600 transition-colors uppercase tracking-widest w-full text-center"
        >
          Discuss Project
        </Link>
      </div>
    </div>
  </motion.div>
);

function ServicesPage() {
  return (
    <div className="relative w-full min-h-screen pt-28 pb-24 overflow-hidden bg-[#f8fafc] text-[#0f172a] font-sans">
      {/* Dynamic Grid Background - Light Theme */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-30 pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Header */}
        <div className="text-left mb-16 max-w-4xl">
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs font-mono font-bold tracking-[0.25em] text-blue-600 uppercase mb-3"
          >
            Engineering Capabilities
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-gray-950 mb-5 leading-tight"
          >
            End-to-End <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Autonomous Systems</span> Development
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600 font-normal max-w-2xl leading-relaxed"
          >
            From custom printed circuit boards to deep-learning edge vision models, we provide full-stack R&D engineering services for enterprises requiring mission-critical hardware and software.
          </motion.p>
        </div>

        {/* Section 1: Hardware & Embedded Systems */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">Hardware & Embedded Systems</h3>
            <div className="h-1 w-16 bg-blue-600 mt-3 rounded-full"></div>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {hardwareServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </motion.div>
        </div>

        {/* Section 2: Software & Autonomous Systems */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">Software & AI Architectures</h3>
            <div className="h-1 w-16 bg-purple-600 mt-3 rounded-full"></div>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {softwareServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </motion.div>
        </div>

      </div>
    </div>
  );
}

export default ServicesPage;
